import { getFilter } from 'data/filters';
import { AVAILABILITY_FILTER_OPTIONS } from 'data/subOptions/availabilitySubOptions';
import addDays from 'date-fns/addDays';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import useStores from 'hooks/useStores';
import AgendaPopover from 'modules/common/Popovers/AgendaPopover/AgendaPopover';
import { PopoverTriggerSource } from 'modules/common/SubBars/ExperienceFilterSubNavbar/usePopoverTrigger';
import routeToSearchURL from 'modules/homescreen/helpers/routeToSearchURL';
import React, { useMemo } from 'react';
import { useTrekker } from 'utils/analytics';
import { showAvailabilityFilter } from 'utils/availabilityFilterHelper';
import formatDate from 'utils/formatDate';
import useLocalizedNavigate from '~/hooks/useLocalizedNavigate';

type AgendaPopoverProps = {
  initialDateFrom?: Date;
  initialDateTo?: Date;
  onClose: ({
    callback,
    source,
  }: {
    callback: () => void;
    source: PopoverTriggerSource;
  }) => void;
};

const stores = ['FiltersStore', 'SearchStore'];
function getStateFromStore(flux) {
  const setFilterDates = (dates) => {
    flux.actions.filters.setDates(dates);
  };

  const setAvailabilityFilter = (availability) => {
    return flux.actions.filters.setAvailabilityFilter({ availability });
  };

  return {
    aggregations: flux.stores.SearchStore.getAggregations(
      flux.stores.FiltersStore.getFilters(),
    ),
    setFilterDates,
    setAvailabilityFilter,
  };
}

function AgendaPopoverContainer({
  initialDateFrom,
  initialDateTo,
  onClose,
}: AgendaPopoverProps) {
  const trekker = useTrekker();
  const { setAvailabilityFilter, aggregations, setFilterDates } = useStores(
    stores,
    getStateFromStore,
  );

  const [dates, setDates] = React.useState({
    startDate: initialDateFrom,
    endDate: initialDateTo,
  });

  const { isPristine, showAvailabilityOptions } = useMemo(() => {
    const { startDate, endDate } = dates;
    return {
      isPristine: startDate === initialDateFrom && endDate === initialDateTo,
      showAvailabilityOptions: showAvailabilityFilter(aggregations),
    };
  }, [dates, aggregations, initialDateFrom, initialDateTo]);

  const navigate = useLocalizedNavigate();

  const onApply = (source) => (e?: any) => {
    e?.stopPropagation();

    if (isPristine) {
      onClose({ callback: () => {}, source });
      return;
    }

    const formattedDates = {
      selectedFrom: !!dates.startDate
        ? formatDate(dates.startDate, 'yyyy-MM-dd')
        : null,
      selectedTo: !!dates.endDate
        ? formatDate(dates.endDate, 'yyyy-MM-dd')
        : null,
    };

    const filtersToAdd = [] as any[];
    const dateFromFilter = getFilter('date.from');
    dateFromFilter.value = formattedDates.selectedFrom || null;
    filtersToAdd.push(dateFromFilter);

    const dateToFilter = getFilter('date.to');
    dateToFilter.value = formattedDates.selectedTo || null;
    filtersToAdd.push(dateToFilter);
    setFilterDates(formattedDates);

    trekker.event('click', {
      action: 'date filter',
      buttonText: 'apply',
      date: `${formattedDates.selectedFrom} / ${
        formattedDates.selectedTo || formattedDates.selectedFrom
      }`,
    });

    onClose({
      callback: () => {
        const { to, replace } = routeToSearchURL({
          filtersToAdd,
          replace: true,
        });
        navigate(to, { replace });
      },
      source,
    });
  };

  const onClear = () => {
    trekker.event('click', {
      action: 'date filter',
      buttonText: 'clear',
    });
    setDates({
      startDate: null,
      endDate: null,
    });
  };

  const onSelectDate = (date) => {
    const { startDate, endDate } = dates;
    let newFrom = startDate;
    let newTo = endDate;
    if (startDate && endDate) {
      newFrom = date;
      newTo = null;
    } else if (
      !startDate ||
      isBefore(date, startDate) ||
      isBefore(date, endDate) ||
      isPristine
    ) {
      newFrom = date;
      newTo = null;
    } else if (startDate && isAfter(date, startDate)) {
      newTo = date;
    }

    setDates({
      startDate: newFrom,
      endDate: newTo,
    });
  };

  const onAvailabilityChange = (availability: AVAILABILITY_FILTER_OPTIONS) => {
    setAvailabilityFilter(availability);

    // set from date to relevant availability date
    const newDate =
      availability === 'tomorrow' ? addDays(new Date(), 1) : new Date();
    const fromDateFilter = getFilter('date.from');
    fromDateFilter.value = formatDate(newDate, 'yyyy-MM-dd');

    // unset to date
    const toDateFilter = getFilter('date.to');
    toDateFilter.value = null;

    // set availability filter
    const availabilityFilter = getFilter('other.availability.items[]');
    availabilityFilter.subOptions = availabilityFilter.subOptions.filter(
      (subOption) => subOption.value === availability,
    );

    onClose({
      callback: () => {
        setFilterDates({
          selectedFrom: formatDate(newDate, 'yyyy-MM-dd'),
          selectedTo: null,
        });
      },
      source: 'availability button',
    });
  };

  const onClickToday = () => {
    trekker.event('click', {
      action: 'date filter',
      buttonText: 'today',
    });
    onAvailabilityChange(AVAILABILITY_FILTER_OPTIONS.TODAY);
  };

  const onClickTomorrow = () => {
    trekker.event('click', {
      action: 'date filter',
      buttonText: 'tomorrow',
    });
    onAvailabilityChange(AVAILABILITY_FILTER_OPTIONS.TOMORROW);
  };

  return (
    <AgendaPopover
      showAvailabilityOptions={showAvailabilityOptions}
      onClickToday={onClickToday}
      onClickTomorrow={onClickTomorrow}
      startDate={dates.startDate}
      endDate={dates.endDate}
      onApply={onApply}
      onClear={onClear}
      onSelectDate={onSelectDate}
    />
  );
}

export default AgendaPopoverContainer;
